/**
 * Professional Radar Chart (Net Diagram / Spider Chart) Component
 * Displays skill profiling based on quiz performance across topics
 * 
 * Shows two overlapping polygons:
 * - Blue: Topic relevance based on questions (baseline/possible)
 * - Orange: User's actual performance (achieved)
 */

import React, { useMemo } from 'react';
import { TOPIC_CATEGORIES } from './ProfessionalQuizData';

// Chart configuration
const CONFIG = {
  size: 320,
  centerX: 160,
  centerY: 160,
  maxRadius: 120,
  levels: 5,
  labelOffset: 28,
  dotRadius: 5,
  colors: {
    baseline: {
      fill: 'rgba(59, 130, 246, 0.2)',      // Blue fill
      stroke: '#3b82f6',                     // Blue stroke
      dot: '#2563eb'                         // Blue dot
    },
    achieved: {
      fill: 'rgba(249, 115, 22, 0.35)',      // Orange fill
      stroke: '#f97316',                      // Orange stroke
      dot: '#ea580c'                          // Orange dot
    },
    grid: {
      line: '#e5e7eb',
      axis: '#9ca3af',
      label: '#374151'
    }
  }
};

/**
 * Calculate point position on radar chart
 */
const getPointPosition = (index, total, value, maxValue, centerX, centerY, maxRadius) => {
  const angle = (Math.PI * 2 * index) / total - Math.PI / 2;
  const radius = (value / maxValue) * maxRadius;
  return {
    x: centerX + radius * Math.cos(angle),
    y: centerY + radius * Math.sin(angle)
  };
};

/**
 * Generate polygon path from points
 */
const generatePolygonPath = (points) => {
  if (points.length === 0) return '';
  const pathData = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  return `${pathData} Z`;
};

/**
 * Grid levels component
 */
const GridLevels = ({ levels, centerX, centerY, maxRadius }) => {
  const gridLevels = [];
  
  for (let level = 1; level <= levels; level++) {
    const radius = (maxRadius / levels) * level;
    gridLevels.push(
      <circle
        key={`grid-${level}`}
        cx={centerX}
        cy={centerY}
        r={radius}
        fill="none"
        stroke={CONFIG.colors.grid.line}
        strokeWidth="1"
        strokeDasharray={level === levels ? "none" : "3,3"}
        opacity={level === levels ? 0.8 : 0.5}
      />
    );
  }
  
  return <g className="grid-levels">{gridLevels}</g>;
};

/**
 * Axis lines and labels component
 */
const AxisLines = ({ topics, centerX, centerY, maxRadius, labelOffset, onTopicClick }) => {
  const topicKeys = Object.keys(topics);
  const total = topicKeys.length;
  
  return (
    <g className="axis-lines">
      {topicKeys.map((key, index) => {
        const angle = (Math.PI * 2 * index) / total - Math.PI / 2;
        const endX = centerX + maxRadius * Math.cos(angle);
        const endY = centerY + maxRadius * Math.sin(angle);
        const labelX = centerX + (maxRadius + labelOffset) * Math.cos(angle);
        const labelY = centerY + (maxRadius + labelOffset) * Math.sin(angle);
        
        const topic = TOPIC_CATEGORIES[key];
        const isClickable = !!onTopicClick;
        
        return (
          <g key={`axis-${key}`}>
            <line
              x1={centerX}
              y1={centerY}
              x2={endX}
              y2={endY}
              stroke={CONFIG.colors.grid.axis}
              strokeWidth="1"
              opacity="0.6"
            />
            <text
              x={labelX}
              y={labelY}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={isClickable ? topic?.color || CONFIG.colors.grid.label : CONFIG.colors.grid.label}
              fontSize="11"
              fontWeight={isClickable ? '600' : '500'}
              style={{ 
                userSelect: 'none', 
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'fill 0.2s, transform 0.2s'
              }}
              onClick={isClickable ? () => onTopicClick(key) : undefined}
              onMouseEnter={isClickable ? (e) => { e.target.style.fill = topic?.color; e.target.style.fontSize = '12px'; } : undefined}
              onMouseLeave={isClickable ? (e) => { e.target.style.fill = topic?.color || CONFIG.colors.grid.label; e.target.style.fontSize = '11px'; } : undefined}
            >
              {topic?.label || key}
            </text>
          </g>
        );
      })}
    </g>
  );
};

/**
 * Data polygon component
 */
const DataPolygon = ({ data, topics, centerX, centerY, maxRadius, colors, showDots = true }) => {
  const topicKeys = Object.keys(topics);
  const total = topicKeys.length;
  
  const points = topicKeys.map((key, index) => {
    const value = data[key] || 0;
    return getPointPosition(index, total, value, 100, centerX, centerY, maxRadius);
  });
  
  const path = generatePolygonPath(points);
  
  return (
    <g className="data-polygon">
      <path
        d={path}
        fill={colors.fill}
        stroke={colors.stroke}
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      {showDots && points.map((point, index) => (
        <circle
          key={`dot-${index}`}
          cx={point.x}
          cy={point.y}
          r={CONFIG.dotRadius}
          fill={colors.dot}
          stroke="#fff"
          strokeWidth="1.5"
        />
      ))}
    </g>
  );
};

/**
 * Percentage labels at grid levels
 */
const PercentageLabels = ({ levels, centerX, centerY, maxRadius }) => {
  const labels = [];
  
  for (let level = 1; level <= levels; level++) {
    const radius = (maxRadius / levels) * level;
    const percentage = (100 / levels) * level;
    labels.push(
      <text
        key={`pct-${level}`}
        x={centerX + 4}
        y={centerY - radius + 4}
        fontSize="9"
        fill="#9ca3af"
        style={{ userSelect: 'none' }}
      >
        {percentage}%
      </text>
    );
  }
  
  return <g className="percentage-labels">{labels}</g>;
};

/**
 * Legend component
 */
const Legend = ({ showBaseline = true, baselineLabel = 'Topic Coverage', achievedLabel = 'Your Performance' }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    marginTop: '16px',
    fontSize: '13px'
  }}>
    {showBaseline && (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{
          width: '16px',
          height: '16px',
          backgroundColor: CONFIG.colors.baseline.fill,
          border: `2px solid ${CONFIG.colors.baseline.stroke}`,
          borderRadius: '3px'
        }} />
        <span style={{ color: '#6b7280' }}>{baselineLabel}</span>
      </div>
    )}
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div style={{
        width: '16px',
        height: '16px',
        backgroundColor: CONFIG.colors.achieved.fill,
        border: `2px solid ${CONFIG.colors.achieved.stroke}`,
        borderRadius: '3px'
      }} />
      <span style={{ color: '#6b7280' }}>{achievedLabel}</span>
    </div>
  </div>
);

/**
 * Topic details breakdown
 */
const TopicDetails = ({ baselineData, achievedData, onTopicClick, topicMetadata = TOPIC_CATEGORIES, maxLabel = 'max' }) => {
  const topicKeys = Object.keys(topicMetadata);
  const isClickable = !!onTopicClick;
  
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      padding: '8px',
      background: '#f9fafb',
      borderRadius: '8px',
      minWidth: '180px',
      marginLeft:'24px'
    }}>
      {topicKeys.map(key => {
        const topic = topicMetadata[key];
        const baseline = baselineData[key] || 0;
        const achieved = achievedData[key] || 0;
        
        return (
          <div 
            key={key} 
            onClick={isClickable ? () => onTopicClick(key) : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 8px',
              background: '#fff',
              borderRadius: '6px',
              border: '1px solid #e5e7eb',
              cursor: isClickable ? 'pointer' : 'default',
              transition: 'all 0.2s ease'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: topic.color,
              flexShrink: 0
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#374151',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {topic.label}
              </div>
              <div style={{
                fontSize: '10px',
                color: '#6b7280'
              }}>
                <span style={{ color: CONFIG.colors.achieved.stroke, fontWeight: '600' }}>
                  {achieved}%
                </span>
                {' / '}
                <span style={{ color: CONFIG.colors.baseline.stroke }}>
                  {baseline}%
                </span>
                <span style={{ color: '#9ca3af', marginLeft: '2px' }}>{maxLabel}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Main RadarChart Component
 * 
 * @param {Object} props
 * @param {Object} props.baselineData - Topic relevance percentages (blue polygon)
 * @param {Object} props.achievedData - User performance percentages (orange polygon)
 * @param {boolean} props.showLegend - Show legend below chart
 * @param {boolean} props.showDetails - Show topic breakdown details
 * @param {string} props.title - Optional chart title
 * @param {Function} props.onTopicClick - Callback when a topic is clicked (optional)
 */
const RadarChart = ({ 
  baselineData, 
  achievedData, 
  showLegend = true, 
  showDetails = true,
  title,
  baselineLabel,
  achievedLabel,
  maxLabel,
  topicMetadata = TOPIC_CATEGORIES,
  onTopicClick
}) => {
  const { size, centerX, centerY, maxRadius, levels, labelOffset } = CONFIG;
  
  // Ensure we have valid data
  const safeBaselineData = useMemo(() => {
    const result = {};
    Object.keys(topicMetadata).forEach(key => {
      result[key] = baselineData?.[key] || 0;
    });
    return result;
  }, [baselineData, topicMetadata]);
  
  const safeAchievedData = useMemo(() => {
    const result = {};
    Object.keys(topicMetadata).forEach(key => {
      result[key] = achievedData?.[key] || 0;
    });
    return result;
  }, [achievedData, topicMetadata]);
  
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      padding: '16px',
      background: '#fff',
      borderRadius: '12px',
      border: '1px solid #e5e7eb'
    }}>
      {title && (
        <h4 style={{
          margin: '0 0 12px 0',
          fontSize: '15px',
          fontWeight: '600',
          color: '#1f2937'
        }}>
          {title}
        </h4>
      )}
      
      {/* Main content: chart + details side by side */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        flexWrap: 'wrap',
        justifyContent: 'center'
      }}>
        <svg 
          width={size} 
          height={size} 
          viewBox={`0 0 ${size} ${size}`}
          style={{ overflow: 'visible' }}
        >
          {/* Background grid levels */}
          <GridLevels 
            levels={levels} 
            centerX={centerX} 
            centerY={centerY} 
            maxRadius={maxRadius} 
          />
          
          {/* Axis lines and labels */}
          <AxisLines 
            topics={topicMetadata}
            centerX={centerX}
            centerY={centerY}
            maxRadius={maxRadius}
            labelOffset={labelOffset}
            onTopicClick={onTopicClick}
          />
          
          {/* Percentage labels */}
          <PercentageLabels 
            levels={levels}
            centerX={centerX}
            centerY={centerY}
            maxRadius={maxRadius}
          />
          
          {/* Baseline polygon (blue - topic coverage) */}
          <DataPolygon
            data={safeBaselineData}
            topics={topicMetadata}
            centerX={centerX}
            centerY={centerY}
            maxRadius={maxRadius}
            colors={CONFIG.colors.baseline}
            showDots={false}
          />
          
          {/* Achieved polygon (orange - user performance) */}
          <DataPolygon
            data={safeAchievedData}
            topics={topicMetadata}
            centerX={centerX}
            centerY={centerY}
            maxRadius={maxRadius}
            colors={CONFIG.colors.achieved}
            showDots={true}
          />
        </svg>
        
        {showDetails && (
          <TopicDetails 
            baselineData={safeBaselineData} 
            achievedData={safeAchievedData}
            topicMetadata={topicMetadata}
            maxLabel={maxLabel}
            onTopicClick={onTopicClick}
          />
        )}
      </div>
      
      {showLegend && <Legend showBaseline={true} baselineLabel={baselineLabel} achievedLabel={achievedLabel} />}
    </div>
  );
};

export default RadarChart;
